import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  MessageSquare, Send, ArrowLeft, User, Home, Calendar, Clock,
  ChevronRight, RefreshCw, Wrench, Building2, Users, Camera, Shield, ZoomIn, X, Download
} from 'lucide-react';
import { messagesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useMessagingSocket } from '../hooks/useMessagingSocket';
import EmojiPicker from './EmojiPicker';
import LivePhotoCapture from './LivePhotoCapture';

const HostMessagingPanel = ({ autoSelectConversationId = null, onUnreadChange = null, fixedCategory = null, refreshKey = 0 }) => {
  const { toast } = useToast();
  const { getAuthToken, user } = useAuth();
  const currentUserId = user?._id || user?.id;

  // State: Category filter — locked when fixedCategory is provided
  const [messageCategory, setMessageCategory] = useState(fixedCategory || 'all'); // all | guest | provider | landlord

  // State: View hierarchy
  const [viewLevel, setViewLevel] = useState('listings'); // listings | threads | chat
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedStage, setSelectedStage] = useState('upcoming');
  const [selectedConversation, setSelectedConversation] = useState(null);

  // State: Messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // State: Loading
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // conversationId -> { senderName }
  const [showCamera, setShowCamera] = useState(false);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const hasAutoSelected = useRef(false);
  const selectedConvRef = useRef(null);

  // Keep ref in sync for WebSocket callbacks
  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);

  // Real-time WebSocket
  const handleNewMessage = useCallback((conversationId, message) => {
    // Add to messages if viewing this conversation
    if (selectedConvRef.current?._id === conversationId) {
      setMessages(prev => {
        if (prev.find(m => m._id === message._id)) return prev;
        return [...prev, message];
      });
    }
    // Update listings conversation preview
    setListings(prev => prev.map(listing => ({
      ...listing,
      conversations: listing.conversations?.map(conv =>
        conv._id === conversationId
          ? { ...conv, lastMessage: message.content?.substring(0, 100), lastMessageAt: message.createdAt }
          : conv
      ),
      stages: Object.fromEntries(
        Object.entries(listing.stages || {}).map(([stage, data]) => [
          stage,
          {
            ...data,
            conversations: data.conversations?.map(conv =>
              conv._id === conversationId
                ? { ...conv, lastMessage: message.content?.substring(0, 100), lastMessageAt: message.createdAt }
                : conv
            )
          }
        ])
      )
    })));
    // Notify parent about unread change (new message arrived)
    if (onUnreadChange) onUnreadChange();
  }, [onUnreadChange]);

  const handleTyping = useCallback((conversationId, senderId, senderName, isTyping) => {
    setTypingUsers(prev => {
      if (isTyping) {
        return { ...prev, [conversationId]: { senderName, senderId } };
      }
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
  }, []);

  const { isConnected, sendTypingIndicator } = useMessagingSocket({
    onNewMessage: handleNewMessage,
    onTyping: handleTyping,
    activeConversationId: selectedConversation?._id
  });

  // Fetch listings with conversations grouped by property
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      let data = await messagesAPI.getConversationsByListing(token);

      // ── Directly merge deal conversations into the __deals__ virtual listing ──
      // This guarantees landlord negotiation chats always appear in the Landlords tab,
      // even if /by-listing misses them due to type mismatches or caching.
      try {
        const guestData = await messagesAPI.getConversations(token, 'guest');
        const dealConvs = guestData.filter(c =>
          c.conversationType === 'deal' &&
          String(c.hostId) !== String(c.guestId)
        );
        if (dealConvs.length > 0) {
          // Find or create the __deals__ listing
          let dealsListing = data.find(l => l.propertyId === '__deals__');
          if (!dealsListing) {
            dealsListing = {
              propertyId: '__deals__',
              propertyTitle: 'Landlord Negotiations',
              propertyImage: null,
              conversations: [],
              unreadCount: 0,
              stages: {
                upcoming:   { conversations: [], unreadCount: 0 },
                'in-stay':  { conversations: [], unreadCount: 0 },
                completed:  { conversations: [], unreadCount: 0 },
                cancelled:  { conversations: [], unreadCount: 0 },
                inquiry:    { conversations: [], unreadCount: 0 },
              }
            };
            data = [...data, dealsListing];
          }
          // Merge, avoiding duplicates by _id
          const existingIds = new Set(dealsListing.conversations.map(c => c._id));
          for (const conv of dealConvs) {
            if (existingIds.has(conv._id)) continue;
            const enriched = {
              ...conv,
              conversationType: 'deal',
              participantType: 'landlord',
              tripStage: 'inquiry',
            };
            dealsListing.conversations.push(enriched);
            dealsListing.stages.inquiry.conversations.push(enriched);
            const unread = conv.unreadGuest || 0;
            dealsListing.unreadCount += unread;
            dealsListing.stages.inquiry.unreadCount += unread;
            existingIds.add(conv._id);
          }
        }
      } catch (dealErr) {
        console.warn('Could not fetch deal conversations:', dealErr);
      }

      setListings(data);

      // Auto-navigate to specific conversation if ID was passed
      if (autoSelectConversationId && !hasAutoSelected.current) {
        for (const listing of data) {
          for (const conv of (listing.conversations || [])) {
            if (conv._id === autoSelectConversationId) {
              setSelectedListing(listing);
              setSelectedConversation(conv);
              // Find the correct stage for this conversation
              const stage = conv.tripStage || 'inquiry';
              setSelectedStage(stage);
              setViewLevel('chat');
              hasAutoSelected.current = true;
              break;
            }
          }
          if (hasAutoSelected.current) break;
        }
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast({ title: 'Error', description: 'Failed to load messages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, toast, autoSelectConversationId]);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const data = await messagesAPI.getConversation(conversationId, token);
      setMessages(data.messages || []);

      // Update the conversation in our local state to mark as read
      setListings(prev => prev.map(listing => {
        const convInListing = listing.conversations?.find(c => c._id === conversationId);
        if (!convInListing) return listing; // Not in this listing, skip

        // Deal convs: host is guestId, so their unread field is unreadGuest
        const isDeal = convInListing.conversationType === 'deal';
        const unreadToRemove = isDeal ? (convInListing.unreadGuest || 0) : (convInListing.unreadHost || 0);
        const updatedStages = { ...listing.stages };

        // Update the correct stage for this conversation
        for (const stage of Object.keys(updatedStages)) {
          const stageData = updatedStages[stage];
          if (!stageData?.conversations) continue;
          const convInStage = stageData.conversations.find(c => c._id === conversationId);
          if (convInStage) {
            updatedStages[stage] = {
              ...stageData,
              conversations: stageData.conversations.map(c =>
                c._id === conversationId ? { ...c, unreadHost: 0, unreadGuest: 0 } : c
              ),
              unreadCount: Math.max(0, (stageData.unreadCount || 0) - unreadToRemove)
            };
            break;
          }
        }

        return {
          ...listing,
          unreadCount: Math.max(0, (listing.unreadCount || 0) - unreadToRemove),
          stages: updatedStages
        };
      }));
      // Opening a conversation marks messages as read on backend — notify parent
      if (onUnreadChange) onUnreadChange();
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    }
  }, [getAuthToken, onUnreadChange]);

  // Polling fallback — refetch messages every 5s when WebSocket is not connected
  useEffect(() => {
    if (isConnected) return; // WebSocket working, no need to poll
    if (!selectedConversation?._id) return;

    const pollInterval = setInterval(() => {
      fetchMessages(selectedConversation._id);
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [isConnected, selectedConversation?._id, fetchMessages]);

  // Also poll listings periodically when WebSocket is disconnected
  useEffect(() => {
    if (isConnected) return;

    const pollInterval = setInterval(() => {
      fetchListings();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [isConnected, fetchListings]);

  // Reset auto-select when conversationId prop changes
  useEffect(() => {
    if (autoSelectConversationId) {
      hasAutoSelected.current = false;
    }
  }, [autoSelectConversationId]);

  // Initial load
  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // Re-fetch when parent signals the Messages tab has been re-activated
  // (refreshKey increments each time user navigates to the Messages tab)
  useEffect(() => {
    if (refreshKey > 0) fetchListings();
  }, [refreshKey, fetchListings]);

  // Load messages when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
    }
  }, [selectedConversation, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handlers
  const handleSelectListing = (listing) => {
    setSelectedListing(listing);
    // Clear the right panel — user must explicitly pick a conversation from center panel
    setSelectedConversation(null);
    setMessages([]);
    // Find first stage with conversations
    const stagesOrder = ['upcoming', 'in-stay', 'completed', 'inquiry'];
    const firstStageWithConvs = stagesOrder.find(s =>
      listing.stages[s]?.conversations?.length > 0
    ) || 'upcoming';
    setSelectedStage(firstStageWithConvs);
    setViewLevel('threads');
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setViewLevel('chat');
  };

  const handleBack = () => {
    if (viewLevel === 'chat') {
      setSelectedConversation(null);
      setMessages([]);
      setViewLevel('threads');
    } else if (viewLevel === 'threads') {
      setSelectedListing(null);
      setViewLevel('listings');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchListings();
    setRefreshing(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const token = getAuthToken();
      const message = await messagesAPI.sendMessage({
        conversationId: selectedConversation._id,
        content: newMessage.trim()
      }, token);

      setMessages(prev => [...prev, message]);
      setNewMessage('');
      // Refocus textarea so Enter key works immediately on next message
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleSendPhoto = async (blob, metadata) => {
    if (!selectedConversation) return;
    setShowCamera(false);
    setSendingPhoto(true);
    try {
      const token = getAuthToken();
      const message = await messagesAPI.sendPhotoMessage(
        selectedConversation._id, blob, 'evidence_photo', metadata, token
      );
      setMessages(prev => [...prev, message]);
      toast({ title: 'Evidence photo sent', description: 'Your timestamped evidence photo has been sent.' });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send photo';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    } finally {
      setSendingPhoto(false);
    }
  };

  // Formatting helpers
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const formatDateRange = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 'No dates';
    const ci = new Date(checkIn);
    const co = new Date(checkOut);
    return `${ci.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${co.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  };

  // Trip stage labels
  const stageLabels = {
    'upcoming': 'Upcoming',
    'in-stay': 'In-Stay',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
    'inquiry': 'Inquiries'
  };

  const stageOrder = ['upcoming', 'in-stay', 'completed', 'cancelled', 'inquiry'];

  // Calculate total unread
  const totalUnread = listings.reduce((sum, l) => sum + (l.unreadCount || 0), 0);

  // Category counts and filtering
  const getCategoryForConv = (conv) => {
    if (conv.conversationType === 'task') return 'provider';
    return conv.participantType || 'guest';
  };

  const categoryCounts = listings.reduce((acc, listing) => {
    (listing.conversations || []).forEach(conv => {
      const cat = getCategoryForConv(conv);
      acc[cat] = (acc[cat] || 0) + 1;
      // Deal convs: R2R host is guestId, so their unread is unreadGuest
      const convUnread = conv.conversationType === 'deal' ? (conv.unreadGuest || 0) : (conv.unreadHost || 0);
      if (convUnread > 0) {
        acc[`${cat}_unread`] = (acc[`${cat}_unread`] || 0) + convUnread;
      }
    });
    return acc;
  }, {});

  // Filter listings by category — only show listings that have conversations matching the category
  // The virtual __tasks__ listing is always provider-category — never show it under Guests
  const filteredListings = messageCategory === 'all'
    ? listings
    : listings.map(listing => {
        // The vendor/task virtual listing always belongs to provider category
        if (listing.propertyId === '__tasks__' && messageCategory !== 'provider') return null;
        // The deals virtual listing always belongs to landlord category
        if (listing.propertyId === '__deals__' && messageCategory !== 'landlord') return null;

        const filteredConvs = (listing.conversations || []).filter(
          c => getCategoryForConv(c) === messageCategory
        );
        if (filteredConvs.length === 0) return null;

        // Rebuild stages for filtered conversations
        const filteredStages = {};
        for (const stage of stageOrder) {
          const stageConvs = filteredConvs.filter(c => c.tripStage === stage);
          filteredStages[stage] = {
            conversations: stageConvs,
            unreadCount: stageConvs.reduce((sum, c) => sum + (c.unreadHost || 0), 0)
          };
        }

        return {
          ...listing,
          conversations: filteredConvs,
          unreadCount: filteredConvs.reduce((sum, c) => sum + (c.unreadHost || 0), 0),
          stages: filteredStages
        };
      }).filter(Boolean);

  const categoryConfig = {
    all:      { label: 'All',       icon: MessageSquare, color: 'text-[#1F3A68]', bg: 'bg-[#1F3A68]' },
    guest:    { label: 'Guests',    icon: User,          color: 'text-emerald-600', bg: 'bg-emerald-500' },
    provider: { label: 'Vendors',   icon: Wrench,        color: 'text-violet-600',  bg: 'bg-violet-500' },
    landlord: { label: 'Landlords', icon: Building2,     color: 'text-amber-600',   bg: 'bg-amber-500' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="host-messaging-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[400px] max-h-[700px] bg-white rounded-lg border overflow-hidden" data-testid="host-messaging-panel">
      {/* LEFT PANEL: Listings List */}
      <div className={`w-full md:w-80 border-r flex flex-col ${viewLevel !== 'listings' ? 'hidden md:flex' : ''}`}>
        {/* Header */}
        <div className="p-3 border-b bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg text-[#1F3A68]">Inbox</h3>
              <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} title={isConnected ? 'Live' : 'Reconnecting...'} />
            </div>
            <div className="flex items-center gap-2">
              {totalUnread > 0 && (
                <Badge variant="destructive" className="bg-[#CB3B3B]" data-testid="total-unread-badge">
                  {totalUnread}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-messages-btn"
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>

          {/* Category Filter Tabs — hidden when fixedCategory locks the view */}
          {!fixedCategory && <div className="flex gap-1">
            {Object.entries(categoryConfig).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const count = key === 'all'
                ? listings.reduce((s, l) => s + (l.conversations?.length || 0), 0)
                : (categoryCounts[key] || 0);
              const unread = key === 'all'
                ? totalUnread
                : (categoryCounts[`${key}_unread`] || 0);
              // Always show All, Guests, Vendors, and Landlords tabs; hide unknown categories if empty
              if (key !== 'all' && key !== 'guest' && key !== 'provider' && key !== 'landlord' && count === 0) return null;

              return (
                <button
                  key={key}
                  onClick={() => {
                    setMessageCategory(key);
                    setSelectedListing(null);
                    setSelectedConversation(null);
                    setViewLevel('listings');
                  }}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    messageCategory === key
                      ? `${cfg.bg} text-white`
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`category-tab-${key}`}
                >
                  <Icon size={12} />
                  <span>{cfg.label}</span>
                  {unread > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0 rounded-full text-[10px] font-bold ${
                      messageCategory === key ? 'bg-white/30 text-white' : 'bg-[#CB3B3B] text-white'
                    }`}>
                      {unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>}
        </div>

        {/* Listings */}
        <div className="flex-1 overflow-y-auto">
          {filteredListings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Home size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="font-medium">No messages yet</p>
              <p className="text-sm mt-1">Messages will appear here</p>
            </div>
          ) : (
            filteredListings.map(listing => (
              <div
                key={listing.propertyId}
                onClick={() => handleSelectListing(listing)}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedListing?.propertyId === listing.propertyId ? 'bg-blue-50 border-l-4 border-l-[#1F3A68]' : ''
                }`}
                data-testid={`listing-item-${listing.propertyId}`}
              >
                <div className="flex items-center gap-3">
                  {/* Property Image */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    {listing.propertyImage ? (
                      <img src={listing.propertyImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home size={20} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Property Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900 truncate text-sm">
                        {listing.propertyTitle || 'Untitled Property'}
                      </span>
                      {listing.unreadCount > 0 && (
                        <Badge variant="destructive" className="bg-[#CB3B3B] ml-2" data-testid={`listing-unread-${listing.propertyId}`}>
                          {listing.unreadCount}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {listing.conversations?.length || 0} conversation{listing.conversations?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  
                  <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* CENTER PANEL: Threads by Stage */}
      <div className={`w-full md:w-80 border-r flex flex-col ${
        viewLevel === 'threads' ? '' : viewLevel === 'chat' ? 'hidden md:flex' : 'hidden'
      }`}>
        {selectedListing && (
          <>
            {/* Header with Back button */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="md:hidden"
                  data-testid="back-to-listings-btn"
                >
                  <ArrowLeft size={20} />
                </Button>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-[#1F3A68] truncate">
                    {selectedListing.propertyTitle}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {selectedListing.conversations?.length || 0} conversations
                  </p>
                </div>
              </div>
            </div>

            {/* Trip Stage Tabs */}
            <Tabs value={selectedStage} onValueChange={setSelectedStage} className="flex flex-col flex-1">
              <TabsList className="flex flex-wrap m-2 h-auto gap-1">
                {stageOrder.map(stage => {
                  const stageData = selectedListing.stages[stage] || { conversations: [], unreadCount: 0 };
                  const hasUnread = stageData.unreadCount > 0;
                  const hasConvs = stageData.conversations?.length > 0;

                  return (
                    <TabsTrigger
                      key={stage}
                      value={stage}
                      className="text-xs py-1.5 px-2 relative data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white flex-1 min-w-0"
                      disabled={!hasConvs}
                      data-testid={`stage-tab-${stage}`}
                    >
                      <span className="truncate">{stageLabels[stage]}</span>
                      {hasUnread && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#CB3B3B] rounded-full" />
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Thread List for each stage */}
              {stageOrder.map(stage => (
                <TabsContent key={stage} value={stage} className="flex-1 overflow-y-auto m-0 mt-0">
                  {(selectedListing.stages[stage]?.conversations || []).length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No {stageLabels[stage].toLowerCase()} conversations</p>
                    </div>
                  ) : (
                    (selectedListing.stages[stage]?.conversations || []).map(conv => (
                      <div
                        key={conv._id}
                        onClick={() => handleSelectConversation(conv)}
                        className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedConversation?._id === conv._id ? 'bg-blue-50' : ''
                        } ${(conv.conversationType === 'deal' ? (conv.unreadGuest || 0) : (conv.unreadHost || 0)) > 0 ? 'bg-amber-50' : ''}`}
                        data-testid={`thread-item-${conv._id}`}
                      >
                        {(() => { const convUnread = conv.conversationType === 'deal' ? (conv.unreadGuest || 0) : (conv.unreadHost || 0); return (
                        <div className="flex items-start gap-3">
                          {/* Unread indicator */}
                          {convUnread > 0 && (
                            <div className="w-2 h-2 bg-[#CB3B3B] rounded-full mt-2 flex-shrink-0" />
                          )}
                          
                          {/* Participant avatar — color-coded by type */}
                          {(() => {
                            const pType = conv.participantType || 'guest';
                            const avatarColors = {
                              guest: 'bg-[#1F3A68]',
                              provider: 'bg-violet-500',
                              landlord: 'bg-amber-500'
                            };
                            const avatarIcons = {
                              guest: <User size={18} />,
                              provider: <Wrench size={18} />,
                              landlord: <Building2 size={18} />
                            };
                            return (
                              <div className={`w-10 h-10 ${avatarColors[pType] || avatarColors.guest} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                                {avatarIcons[pType] || avatarIcons.guest}
                              </div>
                            );
                          })()}

                          {/* Conversation info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className={`text-sm truncate ${(conv.conversationType === 'deal' ? (conv.unreadGuest || 0) : (conv.unreadHost || 0)) > 0 ? 'font-bold' : 'font-medium'}`}>
                                  {conv.conversationType === 'deal' ? (conv.hostName || 'Landlord') : (conv.guestName || 'Guest')}
                                </span>
                                {conv.participantType && conv.participantType !== 'guest' && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                    conv.participantType === 'provider'
                                      ? 'bg-violet-100 text-violet-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {conv.participantType === 'provider' ? 'Provider' : 'Landlord'}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-400">
                                {formatDate(conv.lastMessageAt)}
                              </span>
                            </div>
                            
                            {/* Booking dates */}
                            {conv.checkIn && conv.checkOut && (
                              <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                <Calendar size={10} />
                                <span>{formatDateRange(conv.checkIn, conv.checkOut)}</span>
                              </div>
                            )}
                            
                            {/* Last message preview */}
                            {conv.lastMessage && (
                              <p className={`text-xs mt-1 truncate ${convUnread > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                {conv.lastMessage}
                              </p>
                            )}
                          </div>

                          {/* Unread badge */}
                          {convUnread > 0 && (
                            <Badge variant="destructive" className="bg-[#CB3B3B] text-xs">
                              {convUnread}
                            </Badge>
                          )}
                        </div>
                        ); })()}
                      </div>
                    ))
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
      </div>

      {/* RIGHT PANEL: Chat View */}
      <div className={`flex-1 flex flex-col ${viewLevel === 'chat' ? '' : 'hidden md:flex'}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header with Context */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="md:hidden"
                  data-testid="back-to-threads-btn"
                >
                  <ArrowLeft size={20} />
                </Button>
                
                {(() => {
                  const pType = selectedConversation.participantType || 'guest';
                  const colors = { guest: 'bg-[#1F3A68]', provider: 'bg-violet-500', landlord: 'bg-amber-500' };
                  const icons = { guest: <User size={20} />, provider: <Wrench size={20} />, landlord: <Building2 size={20} /> };
                  return (
                    <div className={`w-10 h-10 ${colors[pType] || colors.guest} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                      {icons[pType] || icons.guest}
                    </div>
                  );
                })()}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-[#1F3A68]">
                    {selectedConversation.conversationType === 'deal'
                      ? (selectedConversation.hostName || 'Landlord')
                      : (selectedConversation.guestName || 'Guest')}
                  </h4>
                    {selectedConversation.participantType && selectedConversation.participantType !== 'guest' && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        selectedConversation.participantType === 'provider'
                          ? 'bg-violet-100 text-violet-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {selectedConversation.participantType === 'provider' ? 'Service Provider' : 'Landlord'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="truncate">{selectedListing?.propertyTitle}</span>
                    {selectedConversation.checkIn && selectedConversation.checkOut && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          {formatDateRange(selectedConversation.checkIn, selectedConversation.checkOut)}
                        </span>
                      </>
                    )}
                    {selectedConversation.bookingRef && (
                      <>
                        <span>•</span>
                        <span className="font-mono">#{selectedConversation.bookingRef}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Trip stage badge */}
                <Badge variant="outline" className="capitalize">
                  {stageLabels[selectedConversation.tripStage] || selectedConversation.tripStage}
                </Badge>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  // For deal convs the host is guestId, so their messages have senderRole='guest'
                  const isOwn = selectedConversation.conversationType === 'deal'
                    ? msg.senderId === currentUserId
                    : msg.senderRole === 'host';
                  const showDate = index === 0 ||
                    formatDate(msg.createdAt) !== formatDate(messages[index - 1]?.createdAt);
                  const isPhoto = msg.messageType === 'photo' || msg.messageType === 'evidence_photo';
                  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

                  return (
                    <React.Fragment key={msg._id || index}>
                      {showDate && (
                        <div className="text-center">
                          <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {formatDate(msg.createdAt)}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] ${
                          isOwn
                            ? 'bg-[#1F3A68] text-white rounded-l-lg rounded-tr-lg'
                            : 'bg-gray-100 text-gray-900 rounded-r-lg rounded-tl-lg'
                        } px-4 py-2`}>
                          {isPhoto ? (
                            <div>
                              {msg.messageType === 'evidence_photo' && (
                                <div className={`flex items-center gap-1 mb-1 text-xs font-semibold ${isOwn ? 'text-yellow-300' : 'text-yellow-600'}`}>
                                  <Shield className="w-3 h-3" /> Evidence Photo
                                </div>
                              )}
                              <div className="relative cursor-pointer group" onClick={() => setFullscreenPhoto(`${API_URL}${msg.photoUrl}`)}>
                                <img
                                  src={`${API_URL}${msg.photoUrl}`}
                                  alt="Photo message"
                                  className="rounded-lg max-w-full max-h-64 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <a
                                  href={`${API_URL}${msg.photoUrl}`}
                                  download={`photo-${msg._id || Date.now()}.jpg`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute top-1 right-1 p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                  onClick={e => e.stopPropagation()}
                                  title="Download photo"
                                >
                                  <Download className="w-3.5 h-3.5 text-[#1F3A68]" />
                                </a>
                              </div>
                              {msg.photoMetadata?.capturedAt && (
                                <div className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                                  Captured: {new Date(msg.photoMetadata.capturedAt).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          )}
                          <div className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                            {formatTime(msg.createdAt)}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {typingUsers[selectedConversation._id] && (
              <div className="px-4 py-1 text-sm text-gray-500 italic animate-pulse">
                {typingUsers[selectedConversation._id].senderName} is typing...
              </div>
            )}

            {/* Message Input */}
            <div className="p-3 border-t bg-gray-50">
              {sendingPhoto && (selectedConversation.participantType === 'provider' || selectedConversation.conversationType === 'task') && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                  Sending evidence photo...
                </div>
              )}
              <div className="flex gap-2 items-end">
                {/* Camera only available for vendor/provider conversations */}
                {(selectedConversation.participantType === 'provider' || selectedConversation.conversationType === 'task') && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 flex-shrink-0 text-gray-500 hover:text-[#1F3A68]"
                    onClick={() => setShowCamera(true)}
                    title="Take evidence photo (timestamped)"
                  >
                    <Camera size={20} />
                  </Button>
                )}
                <EmojiPicker
                  onSelect={(emoji) => {
                    const ta = textareaRef.current;
                    if (ta) {
                      const start = ta.selectionStart || newMessage.length;
                      const end = ta.selectionEnd || newMessage.length;
                      const updated = newMessage.slice(0, start) + emoji + newMessage.slice(end);
                      setNewMessage(updated);
                      setTimeout(() => {
                        ta.focus();
                        const pos = start + emoji.length;
                        ta.setSelectionRange(pos, pos);
                      }, 0);
                    } else {
                      setNewMessage(prev => prev + emoji);
                    }
                  }}
                />
                <Textarea
                  ref={textareaRef}
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    if (selectedConversation?._id && e.target.value.trim()) {
                      sendTypingIndicator(selectedConversation._id, true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className="flex-1 min-h-[40px] max-h-[120px] resize-none"
                  rows={1}
                  data-testid="message-input"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-[#1F3A68] h-10"
                  data-testid="send-message-btn"
                >
                  <Send size={20} />
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Enter to send · Shift+Enter for new line</p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Choose a listing and guest to view messages</p>
            </div>
          </div>
        )}
      </div>

      {/* Live Camera Capture Overlay */}
      {showCamera && (
        <LivePhotoCapture
          role="host"
          onCapture={handleSendPhoto}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Fullscreen Photo Lightbox */}
      {fullscreenPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setFullscreenPhoto(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setFullscreenPhoto(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          <img src={fullscreenPhoto} alt="Full size" className="max-w-[95vw] max-h-[95vh] object-contain" />
        </div>
      )}
    </div>
  );
};

export default HostMessagingPanel;
